import { Form, Rate, Modal, Input } from 'antd';
import IcClose from 'assets/Icons/IcClose';
import Box from 'components/Box';
import { Col } from 'components/Box/Col';
import { Row } from 'components/Box/Row';
import Button from 'components/Button';
import { CheckboxGroup } from 'components/Checkbox';
import Text from 'components/Text';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import sadGif from '../../assets/sad.gif';
import FeedbackSuccessModal, { FeedbackSuccessModalRef } from '../FeedbackSuccessModal';
import { IResponseDataBody } from 'services/response';
import shopApis from 'services/shop/apis';
import { useParams } from 'react-router-dom';

export type FeedbackReasonModalRef = {
  open: (stars: number) => void;
};

type Props = {
  onClearRating: () => void;
};

interface FeedbackReasonFormValues {
  reason?: string[];
  stars: number;
  otherReason?: string;
}

const reasons = [
  { label: 'Long waiting time', value: 'Long waiting time' },
  { label: 'Service quality', value: 'Service quality' },
  { label: 'Staff attitude', value: 'Staff attitude' },
  { label: 'Technician skill', value: 'Technician skill' },
  { label: 'Pricing', value: 'Pricing' },
];

const FeedbackReasonModal = forwardRef<FeedbackReasonModalRef, Props>(({ onClearRating }, ref) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FeedbackReasonFormValues>();
  const useFeedbackSuccessModal = useRef<FeedbackSuccessModalRef>(null);
  const { billId } = useParams<{ billId: string }>();

  useImperativeHandle(ref, () => ({
    open: (stars: number) => {
      form.setFieldValue('stars', stars);
      setOpen(true);
    },
  }));

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: FeedbackReasonFormValues) => {
    const reason = `${(values.reason || [])?.join(', ')}${values.otherReason ? `, ${values.otherReason}` : ''}`;

    const res: IResponseDataBody = await shopApis.feedbackReview({
      billId: billId || '',
      reason: reason,
      stars: values.stars,
    });

    if (res?.data?.data) {
      useFeedbackSuccessModal.current?.open();
      handleClose();
      onClearRating();
    }
  };

  return (
    <>
      <StyledModal
        open={open}
        onCancel={handleClose}
        title={null}
        footer={null}
        destroyOnClose
        width={400}
        closable={false}
        bodyStyle={{
          padding: 0,
        }}
      >
        <Header>
          <Text variant='H7'>Reason</Text>
          <button onClick={handleClose}>
            <IcClose width={28} height={28} />
          </button>
        </Header>
        <Form form={form} layout='vertical' className='feedback-reason-form' onFinish={handleSubmit}>
          <Box display='flex' alignItems='center' justifyContent='center'>
            <Form.Item name='stars' noStyle>
              <Rate className='feedback-rate' disabled />
            </Form.Item>
          </Box>
          <Col gap='1'>
            <Row alignItems='center' gap='1'>
              <img src={sadGif} alt='sad' width={28} height={28} />
              <Text variant='H8' weight='medium'>
                We’re sorry to hear that
              </Text>
            </Row>
            <Text weight='medium' color='text_3_v2'>
              Please let us know what we can improve
            </Text>
          </Col>
          <Form.Item name='reason' noStyle>
            <CheckboxGroup options={reasons} direction='column' />
          </Form.Item>
          <Form.Item name='otherReason' label='Others'>
            <Input.TextArea placeholder='Please tell us more...' rows={4} />
          </Form.Item>
        </Form>
        <Action>
          <Button ntype='TERTIARY' onClick={form.submit} style={{ width: '100%' }}>
            Submit Feedback
          </Button>
        </Action>
      </StyledModal>
      <FeedbackSuccessModal ref={useFeedbackSuccessModal} />
    </>
  );
});

FeedbackReasonModal.displayName = 'FeedbackReasonModal';
export default FeedbackReasonModal;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0px;
  }

  .ant-modal-body {
    display: flex;
    flex-direction: column;
    max-height: 70vh!important;
  }

  .feedback-reason-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-family: Mona Sans;
    padding: 16px;
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    .ant-form-item-label > label {
      font-size: 14px;
      font-weight: 500;
      font-family: Mona Sans;
    }
  }

  .feedback-rate {
    font-size: 40px;
    line-height: 1;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 8px;
  border-bottom: 1px solid #e9edf2;
`;

const Action = styled.div`
  width: 100%;
  padding: 16px;
  border-top: 1px solid #e9edf2;
  background: #fff;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 0 0 8px 8px;
  .CONTENT_2 {
    font-size: 16px;
    font-family: Mona Sans;
  }
`;
